import { LitElement, html, css, type PropertyValues } from 'lit';
import { property, state } from 'lit/decorators.js';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { styleMap } from 'lit/directives/style-map.js';
import { getResolver, getLocalIcon, hasLocalIcon } from './library.js';
import { isBundleRegistered, isBundleLoaded, loadIconBundle, getBundledIconSvg } from './sprite.js';
import * as t from '../styles/tokens.js';
import type { IconSize } from '../types.js';

// Keyword sizes resolve to the global `--uwc-icon-size-*` tokens (which scale
// with the Theme Designer's font scale). Any other value is treated as a raw
// CSS length and passed through untouched (back-compat for "20px", "1.5em", …).
const ICON_SIZE_KEYWORDS: Record<string, string> = {
  xs: t.iconSizeXs.cssText,
  sm: t.iconSizeSm.cssText,
  md: t.iconSizeMd.cssText,
  lg: t.iconSizeLg.cssText,
  xl: t.iconSizeXl.cssText,
};

export class UwcIcon extends LitElement {
  static override styles = css`
    :host {
      display: inline-flex;
      vertical-align: middle;
      justify-content: center;
      align-items: center;
    }

    /* This ensures the injected SVG obeys the host's size and color */
    .icon-container, 
    .icon-container svg {
      display: block;
      width: var(--icon-size);
      height: var(--icon-size);
      fill: var(--icon-color);
    }

    /* Spin animation for loading icons */
    @keyframes spin {
      from {
        transform: rotate(0deg);
      }
      to {
        transform: rotate(360deg);
      }
    }

    .icon-container.spin svg {
      animation: spin 1s linear infinite;
    }
  `;

  @property({ reflect: true }) name = '';
  @property({ reflect: true }) library = 'default';
  /**
   * Icon size. Either a keyword (`xs | sm | md | lg | xl`) backed by the
   * `--uwc-icon-size-*` tokens, or any CSS length (`"20px"`, `"1.5em"`, …).
   * Defaults to `1em` so a bare icon inherits and scales with its parent's
   * font-size — including a parent control's per-instance `size` and the global
   * Theme Designer scale.
   */
  @property({ reflect: true }) size: IconSize = '1em';
  @property({ reflect: true }) color = 'currentColor';
  @property({ type: Boolean, reflect: true }) spin = false;
  @property() src?: string;

  @state() private svgContent = '';
  @state() private isLocalIcon = false;

  protected override updated(changedProperties: PropertyValues) {
    if (changedProperties.has('name') || changedProperties.has('library') || changedProperties.has('src')) {
      this.loadIcon();
    }
  }

  private async loadIcon() {
    // Priority 1: Check if custom src is provided
    if (this.src) {
      await this.fetchRemoteIcon(this.src);
      return;
    }

    // Priority 2: Check local registry for critical icons
    if (hasLocalIcon(this.name)) {
      this.loadLocalIcon();
      return;
    }

    // Priority 3: Bundle cache (Iconify JSON pre-loaded via registerIconBundle)
    if (isBundleRegistered(this.library)) {
      if (!isBundleLoaded(this.library)) await loadIconBundle(this.library);
      const bundleSvg = getBundledIconSvg(this.library, this.name);
      if (bundleSvg) {
        this.svgContent = this.cleanSvg(bundleSvg);
        this.dispatchEvent(new CustomEvent('uwc-load', { detail: { source: 'bundle' } }));
        return;
      }
    }

    // Priority 4: Resolver — may return a URL or raw SVG markup
    const resolved = this.resolveUrl();
    if (resolved) {
      if (resolved.trimStart().startsWith('<')) {
        this.isLocalIcon = false;
        this.svgContent = this.cleanSvg(resolved);
        this.dispatchEvent(new CustomEvent('uwc-load', { detail: { source: 'inline' } }));
      } else {
        await this.fetchRemoteIcon(resolved);
      }
    } else {
      this.svgContent = '';
    }
  }

  private loadLocalIcon() {
    const localSvg = getLocalIcon(this.name);
    if (localSvg) {
      this.isLocalIcon = true;
      this.svgContent = this.cleanSvg(localSvg);
      this.dispatchEvent(new CustomEvent('uwc-load', { 
        detail: { source: 'local-registry' } 
      }));
    }
  }

  private async fetchRemoteIcon(url: string) {
    try {
      const response = await fetch(url, { mode: 'cors' });
      if (response.ok) {
        const rawSvg = await response.text();
        this.isLocalIcon = false;
        this.svgContent = this.cleanSvg(rawSvg);
        this.dispatchEvent(new CustomEvent('uwc-load', { 
          detail: { source: 'remote' } 
        }));
      } else {
        throw new Error(`Failed to fetch icon: ${response.status}`);
      }
    } catch (err) {
      this.svgContent = '';
      this.dispatchEvent(new CustomEvent('uwc-error', { 
        detail: { err, iconName: this.name } 
      }));
    }
  }

  private cleanSvg(rawSvg: string): string {
    // If the fetched SVG has hardcoded width/height, they can override our CSS.
    // We strip them out to let our container control the dimensions.
    return rawSvg.replace(/<svg([^>]*)>/, (_match, attrs) => {
      const cleanedAttrs = attrs
        .replace(/\b(width|height|fill)\b="[^"]*"/g, '')
        .trim();
      return `<svg ${cleanedAttrs}>`;
    });
  }

  private resolveUrl(): string {
    const resolver = getResolver(this.library);
    return resolver ? resolver(this.name) : '';
  }

  /** Map a keyword size to its token; pass any raw CSS length through unchanged. */
  private resolveSize(): string {
    return ICON_SIZE_KEYWORDS[this.size] ?? this.size;
  }

  override render() {
    const containerStyles = {
      '--icon-size': this.resolveSize(),
      '--icon-color': this.color
    };

    const shouldSpin = this.spin || this.name === 'loading' || this.name === 'spinner';
    const containerClass = shouldSpin ? 'icon-container spin' : 'icon-container';

    return html`
      <div
        class="${containerClass}"
        style=${styleMap(containerStyles)}
        role="img"
        aria-hidden="true"
        data-source="${this.isLocalIcon ? 'local' : 'remote'}"
      >
        ${unsafeSVG(this.svgContent)}
      </div>
    `;
  }
}